export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type LabelSide = 'left' | 'right' | 'above' | 'below';

interface PlaceLabelParams {
  /** Screen-space rect of the annotation the label describes (y grows downward). */
  anchor: Rect;
  size: { width: number; height: number };
  /** The area the label must stay inside, typically the viewport canvas. */
  bounds: Rect;
  /** Areas the label should avoid covering, e.g. the tissue of a mammogram. */
  keepOut?: Rect[];
  /** Space between the anchor and the label. */
  gap?: number;
}

interface Placement {
  side: LabelSide;
  rect: Rect;
}

// Order doubles as the tie-break: left first matches how labels were always drawn.
const SIDES: LabelSide[] = ['left', 'right', 'above', 'below'];

// How far (in label sizes) a side may slide along its cross axis to dodge something in the way.
// 0 first, so an unobstructed label stays centered on the annotation.
const NUDGES = [0, 1, -1, 2, -2];

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function overlapArea(a: Rect, b: Rect): number {
  return (
    Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) *
    Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top))
  );
}

/** Total distance the rect sticks out past bounds, 0 when fully inside. */
function overflow(rect: Rect, bounds: Rect): number {
  return (
    Math.max(0, bounds.left - rect.left) +
    Math.max(0, rect.right - bounds.right) +
    Math.max(0, bounds.top - rect.top) +
    Math.max(0, rect.bottom - bounds.bottom)
  );
}

function moveTo(rect: Rect, left: number, top: number): Rect {
  return {
    left,
    top,
    right: left + (rect.right - rect.left),
    bottom: top + (rect.bottom - rect.top),
  };
}

function candidateFor(
  side: LabelSide,
  anchor: Rect,
  { width, height }: PlaceLabelParams['size'],
  gap: number,
  bounds: Rect,
  nudge: number
): Rect {
  const centerX = (anchor.left + anchor.right) / 2;
  const centerY = (anchor.top + anchor.bottom) / 2;
  const base = { left: 0, top: 0, right: width, bottom: height };

  // Only the cross axis is slid into bounds, so a side never jumps across the anchor.
  switch (side) {
    case 'left':
      return moveTo(
        base,
        anchor.left - gap - width,
        clamp(centerY - height / 2 + nudge * height, bounds.top, bounds.bottom - height)
      );
    case 'right':
      return moveTo(
        base,
        anchor.right + gap,
        clamp(centerY - height / 2 + nudge * height, bounds.top, bounds.bottom - height)
      );
    case 'above':
      return moveTo(
        base,
        clamp(centerX - width / 2 + nudge * width, bounds.left, bounds.right - width),
        anchor.top - gap - height
      );
    case 'below':
      return moveTo(
        base,
        clamp(centerX - width / 2 + nudge * width, bounds.left, bounds.right - width),
        anchor.bottom + gap
      );
  }
}

/**
 * Picks the side of an annotation to draw its label on so the label stays inside `bounds`
 * and covers as little of `keepOut` as possible. Pure and in screen space, so it works for any
 * viewport size (e.g. tiles) and can be tested without VTK.
 *
 * A side that is blocked may slide along its cross axis before another side is considered.
 * If no side fits fully, the least-overflowing side is clamped into bounds.
 */
export function placeLabel({
  anchor,
  size,
  bounds,
  keepOut = [],
  gap = 0,
}: PlaceLabelParams): Placement {
  const candidates = SIDES.flatMap((side) =>
    NUDGES.map((nudge) => {
      const rect = candidateFor(side, anchor, size, gap, bounds, nudge);
      return {
        side,
        rect,
        overflow: overflow(rect, bounds),
        covered: keepOut.reduce((sum, area) => sum + overlapArea(rect, area), 0),
      };
    })
  );

  const best = candidates.reduce((a, b) => {
    if (b.overflow !== a.overflow) return b.overflow < a.overflow ? b : a;
    return b.covered < a.covered ? b : a;
  });

  return {
    side: best.side,
    rect: moveTo(
      best.rect,
      clamp(best.rect.left, bounds.left, bounds.right - size.width),
      clamp(best.rect.top, bounds.top, bounds.bottom - size.height)
    ),
  };
}

export function isSameRect(a: Rect | null, b: Rect | null): boolean {
  return (
    a === b ||
    (a != null &&
      b != null &&
      a.left === b.left &&
      a.right === b.right &&
      a.top === b.top &&
      a.bottom === b.bottom)
  );
}

/** Shifts a rect so its origin moves by (-dx, -dy). */
export function offsetRect(rect: Rect, dx: number, dy: number): Rect {
  return {
    left: rect.left - dx,
    right: rect.right - dx,
    top: rect.top - dy,
    bottom: rect.bottom - dy,
  };
}

/**
 * Screen-space bounding rect (y up) of all eight corners of a world-space box. All corners are
 * projected because in sagittal or coronal views the box's axes don't line up with the screen's.
 */
export function projectBoundsToRect(
  bounds: readonly [number, number, number, number, number, number],
  project: (x: number, y: number, z: number) => readonly number[]
): Rect {
  const [xMin, xMax, yMin, yMax, zMin, zMax] = bounds;
  const points = [xMin, xMax].flatMap((x) =>
    [yMin, yMax].flatMap((y) => [zMin, zMax].map((z) => project(x, y, z)))
  );
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);

  return {
    left: Math.min(...xs),
    right: Math.max(...xs),
    bottom: Math.min(...ys),
    top: Math.max(...ys),
  };
}

export interface PlacementArea {
  areaWidth: number;
  areaHeight: number;
  /** The annotation's rect in CSS px with y growing downward, matching placeLabel. */
  anchor: Rect;
}

/** Converts a display-space rect (device px, y up) and area size into placeLabel's space. */
export function toPlacementArea({
  anchorRect,
  areaSize,
  devicePixelRatio,
}: {
  anchorRect: Rect;
  areaSize: { width: number; height: number };
  devicePixelRatio: number;
}): PlacementArea {
  const areaHeight = areaSize.height / devicePixelRatio;
  return {
    areaWidth: areaSize.width / devicePixelRatio,
    areaHeight,
    anchor: {
      left: anchorRect.left / devicePixelRatio,
      right: anchorRect.right / devicePixelRatio,
      top: areaHeight - anchorRect.top / devicePixelRatio,
      bottom: areaHeight - anchorRect.bottom / devicePixelRatio,
    },
  };
}

/**
 * Places a measured label inside a PlacementArea. `outer` includes the label's border padding;
 * `left`/`bottom` are the CSS offsets (bottom measured from the area's bottom edge) of the
 * content itself.
 */
export function placeLabelInArea({
  area,
  contentSize,
  borderOffset,
  keepOut,
}: {
  area: PlacementArea;
  contentSize: { width: number; height: number };
  borderOffset: number;
  keepOut: Rect[];
}): { outer: Rect; left: number; bottom: number } {
  const { rect: outer } = placeLabel({
    anchor: area.anchor,
    size: {
      width: contentSize.width + borderOffset * 2,
      height: contentSize.height + borderOffset * 2,
    },
    bounds: { left: 0, top: 0, right: area.areaWidth, bottom: area.areaHeight },
    keepOut,
  });

  return {
    outer,
    left: outer.left + borderOffset,
    bottom: area.areaHeight - (outer.bottom - borderOffset),
  };
}
